import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { cn } from "@strivelabs/ui-core"

/**
 * The property hover card: one small card above a cell, saying what the cell
 * had to shorten or could only hint at — a title the row cut, the labels behind
 * a `+N`, a person's full name, how far off a date is.
 *
 * One card for the whole table, because only one cell is ever under the
 * pointer. It is fixed to the viewport rather than placed in the row: the row's
 * cells clip and the list scrolls, and neither may cut a card in half. It waits
 * the platform tooltip's 450ms, and keeps its 300ms warm window, so reading
 * along a row is slow once and immediate after.
 */

const OPEN_DELAY_MS = 450
const WARM_WINDOW_MS = 300
const EDGE = 8

/** Where the card hangs from its cell. */
export type ListTableHoverAnchor = "center" | "start"

interface Showing {
  target: HTMLElement
  content: ReactNode
  anchor: ListTableHoverAnchor
}

interface HoverCardApi {
  enter: (target: HTMLElement, content: ReactNode, anchor: ListTableHoverAnchor) => void
  leave: () => void
}

const HoverCardContext = createContext<HoverCardApi | null>(null)

export const ListTableHoverCardProvider = ({ children }: { children: ReactNode }) => {
  const [ showing, setShowing ] = useState<Showing | null>(null)
  const [ place, setPlace ] = useState<{ left: number; top: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hiddenAt = useRef(0)
  const card = useRef<HTMLDivElement>(null)

  const leave = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    setShowing((held) => {
      if (held) hiddenAt.current = Date.now()
      return null
    })
    setPlace(null)
  }, [])

  const enter = useCallback((target: HTMLElement, content: ReactNode, anchor: ListTableHoverAnchor) => {
    leave()
    const warm = Date.now() - hiddenAt.current < WARM_WINDOW_MS
    timer.current = setTimeout(() => {
      timer.current = null
      setShowing({ target, content, anchor })
    }, warm ? 0 : OPEN_DELAY_MS)
  }, [ leave ])

  // Placed after it has rendered, because only a rendered card knows its size.
  useLayoutEffect(() => {
    if (!showing || !card.current) return
    const cell = showing.target.getBoundingClientRect()
    const box = card.current.getBoundingClientRect()
    const viewport = document.documentElement.clientWidth || window.innerWidth
    const x = showing.anchor === "start" ? cell.left : cell.left + cell.width / 2 - box.width / 2

    setPlace({
      left: Math.max(EDGE, Math.min(x, viewport - box.width - EDGE)),
      top: Math.max(EDGE, cell.top - EDGE - box.height),
    })
  }, [ showing ])

  // A fixed card cannot follow its row, so anything that moves the row closes it.
  useEffect(() => {
    if (!showing) return undefined
    window.addEventListener("scroll", leave, true)
    window.addEventListener("resize", leave)

    return () => {
      window.removeEventListener("scroll", leave, true)
      window.removeEventListener("resize", leave)
    }
  }, [ showing, leave ])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const api = useMemo(() => ({ enter, leave }), [ enter, leave ])

  return (
    <HoverCardContext.Provider value={api}>
      {children}
      {showing && typeof document !== "undefined" ? createPortal(
        <div
          ref={card}
          role="tooltip"
          className={cn(
            "pointer-events-none fixed left-0 top-0 z-[80] box-border max-w-[240px] rounded-v2-xl border border-v2-list-line",
            // A label's card is as wide as the names it holds, up to a ceiling;
            // a title's is wider, because a title is.
            "has-[[data-hc=fix]]:max-w-[200px] has-[[data-hc=wide]]:max-w-[420px]",
            "bg-v2-card p-[3px] shadow-v2-list-pop",
          )}
          style={{
            transform: place ? `translate(${place.left}px, ${place.top}px)` : undefined,
            visibility: place ? "visible" : "hidden",
          }}
        >
          {showing.content}
        </div>,
        document.body,
      ) : null}
    </HoverCardContext.Provider>
  )
}

interface ListTableHoverTargetProps {
  /** What the card says. Nothing means no card. */
  card: ReactNode
  /**
   * `clipped` opens the card only when the target's own text has been cut —
   * a title that fits says nothing more by being shown again.
   */
  when?: "always" | "clipped"
  anchor?: ListTableHoverAnchor
  className?: string
  children: ReactNode
}

/**
 * A cell part that carries a hover card. Outside a `ListTable` it renders its
 * children and nothing else, so the cell parts work on their own too.
 */
export const ListTableHoverTarget = ({
  card, when = "always", anchor = "center", className, children,
}: ListTableHoverTargetProps) => {
  const api = useContext(HoverCardContext)
  const ref = useRef<HTMLSpanElement>(null)

  return (
    <span
      ref={ref}
      className={className}
      onMouseEnter={() => {
        const target = ref.current
        if (!api || !target || !card) return
        if (when === "clipped" && target.scrollWidth <= target.clientWidth + 0.5) return
        api.enter(target, card, anchor)
      }}
      onMouseLeave={() => api?.leave()}
      onClick={() => api?.leave()}
    >
      {children}
    </span>
  )
}

/** One line of a hover card: an optional mark, then the words. */
export const ListTableHoverRow = ({ icon, children, className }: {
  icon?: ReactNode
  children: ReactNode
  className?: string
}) => (
  <div
    className={cn(
      "flex items-center gap-[7px] whitespace-nowrap px-[7px] py-[3px] text-[12px] leading-[18px] text-v2-list-ink",
      className,
    )}
  >
    {icon ? (
      <span className="inline-flex size-[14px] shrink-0 items-center justify-center text-v2-list-faint [&_svg]:size-[14px]">
        {icon}
      </span>
    ) : null}
    <span className="min-w-0 overflow-hidden text-ellipsis">{children}</span>
  </div>
)
